import * as echarts from 'echarts';
import i18n from '@/i18nSetup';
import utils from "@/utils";
import { enableTouchTooltip, bindChartResize, applyChartMotion, resolveWhenDrawn } from './echarts-touch';

const localeMap = {
    "likes": "ALL_LIKES",
    "reposts": "ALL_REPOSTS",
    "comments": "ALL_COMMENTS",
    "views": "ALL_VIEWS",
    "impressions": "ALL_VIEWS",
    "posts": "ALL_RECORDS",
    "stories": 'STORIES',
    "interactions": "INTERACTIONS",
};

// Ensure every value is defined; replace undefined values with 0
const aggregate = (pages, names, locale, vertical = false) => {
    const data = [];
    for (const name of names) {
        const value = {};
        for (const [index, page] of pages.entries()) {
            // Assign value or set it to 0 if undefined
            value['value' + index] = page.stats.total[name]
                ? parseInt(page.stats.total[name].value.replace(/\s+/g, ""))
                : 0;
        }
        // Calculate percentages based on the max value
        const arr = Object.values(value);
        const max = Math.max(...arr);
        for (let key of Object.keys(value)) {
            value[key + '_percent'] = max > 0 ? value[key] / max : 0; // Avoid NaN if max is 0
        }
        value.category = i18n.t('STATS_TABLE_TOTAL_' + (localeMap[name] || name.toUpperCase()));
        data.push(value);
    }

    // Horizontal layout shows first metric at the top; vertical keeps names order left-to-right
    return vertical ? data : data.reverse();
};

const barLayout = (vertical, pageCount) => {
    if (vertical) {
        return {
            barMaxWidth: 4,
            barGap: pageCount > 6 ? '8%' : '20%',
            barCategoryGap: pageCount > 6 ? '22%' : '32%',
        };
    }
    return {
        barWidth: 4,
        barGap: '100%',
    };
};

const buildAxes = (categoryData, vertical) => {
    const valueAxis = {
        type: 'value',
        show: false
    };
    const categoryAxis = {
        type: 'category',
        data: categoryData,
        inverse: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
            show: !vertical,
            fontSize: 12,
            formatter: function (value) {
                return value;
            }
        }
    };

    if (vertical) {
        return { xAxis: categoryAxis, yAxis: valueAxis };
    }
    return { xAxis: valueAxis, yAxis: categoryAxis };
};

const buildSeries = (pages, categoryField, valueField, vertical) => {
    const layout = barLayout(vertical, pages.length);
    return pages.map((page, index) => ({
        name: page.owner_name_medium,
        type: 'bar',
        encode: vertical
            ? { x: categoryField, y: valueField(index) }
            : { x: valueField(index), y: categoryField },
        itemStyle: {
            color: page.colorCode,
            borderRadius: 3,
        },
        ...layout,
        label: {
            show: false
        }
    }));
};

const create = ({ pages, ref, names, data, locale, animation = true, vertical = false }) => {
    return new Promise((resolve, reject) => {
        if (!ref) return;

        let chartData, categoryField, valueField, isNamesMode;
        if (names) {
            chartData = aggregate(pages, names, locale, vertical);
            categoryField = 'category';
            valueField = (index) => `value${index}_percent`;
            isNamesMode = true;
        } else {
            chartData = data;
            categoryField = 'age';
            valueField = (index) => `proc_${index}`;
            isNamesMode = false;
        }

        const chart = echarts.init(ref);

        const categoryData = chartData.map(item => item[categoryField]);

        const dimensions = [categoryField];
        pages.forEach((page, index) => {
            dimensions.push(valueField(index));
        });

        const dataset = {
            dimensions: dimensions,
            source: chartData
        };

        const series = buildSeries(pages, categoryField, valueField, vertical);
        const { xAxis, yAxis } = buildAxes(categoryData, vertical);

        // Tooltip with proper 0 value handling
        const tooltip = {
            trigger: 'axis',
            triggerOn: 'mousemove|click',
            confine: true,
            backgroundColor: 'transparent',
            borderWidth: 0,
            padding: 0,
            transitionDuration: 0.4,
            axisPointer: {
                type: 'shadow'
            },
            formatter: function (params) {
                const category = params[0].axisValue;
                const dataIndex = params[0].dataIndex;
                const dataItem = chartData[dataIndex];
                let tooltipHtml = `<div class='tooltip'><span class='date'>${category}</span><ul class='tooltip-list'>`;
                
                pages.forEach((page, idx) => {
                    let value, displayValue;
                    if (isNamesMode) {
                        const valueFieldName = 'value' + idx;
                        value = dataItem[valueFieldName] || 0; 
                        displayValue = value;
                    } else {
                        // For ages charts, display percentages
                        const valueFieldName = valueField(idx);
                        value = dataItem[valueFieldName] || 0;
                        displayValue = value + '%';
                    }
                    
                    tooltipHtml += `<li data-value="${value}">
                        <div class="soc-sea ${page.colorName}">
                            <span class="soc-ico soc-${page.social.toLowerCase().toLocaleString('ru-RU')}"></span>
                            ${page.owner_name_medium}
                        </div>${displayValue.toLocaleString('ru-RU')}
                    </li>`;
                });
                tooltipHtml += '</ul></div>';
                
                // Check if all values are zero and return an empty string to hide the tooltip
                const totalValue = Object.keys(dataItem)
                    .filter(key => key.includes('value') || key.includes('percent'))
                    .reduce((sum, key) => sum + (dataItem[key] || 0), 0);

                return totalValue === 0 ? '' : tooltipHtml; // Hide tooltip if no valid data
            }
        };

        const option = {
            dataset: dataset,
            tooltip: tooltip,
            xAxis: xAxis,
            yAxis: yAxis,
            grid: vertical
                ? { top: 8, left: 0, right: 0, bottom: 4, containLabel: false }
                : { top: 0, left: 0, right: 0, bottom: 0 },
            series: series,
            animation: animation,
            animationDuration: animation ? 1000 : 0,
            animationEasing: animation ? 'cubicOut' : 'linear',
            animationThreshold: animation ? 2000 : 0
        };

        applyChartMotion(option, animation);
        chart.setOption(option);
        enableTouchTooltip(chart);

        bindChartResize(chart);
        resolveWhenDrawn(chart, resolve);
    });
};

export async function exportChart({
  chart,
  container,
  pages,
  names,
  type = 'svg',
  vertical = false
}) {
  if (!chart) {
    return;
  }

  const filename = `horizontal_chart_${type === 'csv' ? 'data_' : ''}${new Date().toISOString().split('T')[0]}`;

  const chartData = aggregate(pages, names, undefined, vertical);

  if (type === 'svg') {
    if (!container) {
      return;
    }

    try {
      // First dispose the current chart
      if (chart && chart.dispose) {
        chart.dispose();
      }
      
      // Remove any existing chart instances on the container
      echarts.dispose(container);
      const svgChart = echarts.init(container, null, { renderer: 'svg' });
      const categoryData = chartData.map(item => item.category);
      const valueField = (index) => `value${index}_percent`;

      const dimensions = ['category'];
      pages.forEach((page, index) => {
        dimensions.push(valueField(index));
      });

      const dataset = {
        dimensions: dimensions,
        source: chartData
      };

      const series = buildSeries(pages, 'category', valueField, vertical);
      const { xAxis, yAxis } = buildAxes(categoryData, vertical);
      if (vertical) {
        xAxis.axisLabel.show = true;
      }

      const option = {
        dataset: dataset,
        xAxis,
        yAxis,
        grid: vertical
          ? { top: 10, left: 10, right: 10, bottom: 40, containLabel: true }
          : {
              top: 10,
              left: 100,
              right: 10,
              bottom: 10
            },
        series: series,
        animation: false
      };
      
      svgChart.setOption(option);
      
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const svgElement = container.querySelector('svg');
      if (!svgElement) {
        throw new Error("SVG element not found in container");
      }
      
      const svgContent = svgElement.outerHTML;
      const blob = new Blob([svgContent], { type: 'image/svg+xml' });
      utils.downloadFile(blob, `${filename}.svg`);
      if (svgChart) {
        svgChart.dispose();
      }
      
      const newChart = await create({
        pages,
        ref: container,
        names,
        animation: false,
        vertical
      });
      
      return newChart;
    } catch (error) {      
      try {
        return await create({
          pages,
          ref: container,
          names,
          animation: false,
          vertical
        });
      } catch (revertError) {
      }
    }
  } else if (type === 'csv') {
    const categories = chartData.map(item => item.category);
    const series = [];
    pages.forEach((page, index) => {
      series.push({
        name: page.owner_name_medium,
        data: chartData.map(item => item[`value${index}`] || 0)
      });
    });
    
    utils.exportDataToCSV({
      series,
      categories,
      seriesNames: series.map(s => s.name),
      filename
    });
  }
}

export default create;
